/**
 * Wave 6.2 — Adverse-Action review thin landing.
 *
 * Mounted at `/compliance/adverse-review/:cartId`. Per the RCP-only
 * scaffolding rule (memory project_rcp_only_scaffolding_allowed.md) every
 * FSR surface must be functional in v3.0 — but adverse-action letter generation is
 * explicitly Wave 6.3 scope. This landing therefore:
 *
 *   1. Loads the cart payload via `client.retail.carts.get(cartId)` so the FSR
 *      sees the full context (origin, branch, applicants, products,
 *      resolution reason).
 *   2. Surfaces a placeholder workflow card explaining the next step
 *      (letter dispatch) is being added in Wave 6.3.
 *   3. Plumbs entry points + admin/config hooks (back link, restore button)
 *      so the Wave-6.3 builder slots into a real shell.
 *
 * No GUIDs / Cart IDs leak into UI text — the title resolves the applicant
 * from the cart payload, falling back to "Anonymous applicant".
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { Button, Pill } from '@/components/primitives';
import { useApi } from '@/modules/RAO/hooks/useApi';
import { summarizePrimaryApplicant } from '@/modules/RAO/utils/cartCardData';
import { humanizeOriginTier } from '@/modules/RAO/utils/cartResolutionMatrix';
import type { CartDto, RestoreCartResponse } from '@retail-ops/shared-types';
import { ApiError } from '@retail-ops/api-client';

type State =
  | { kind: 'loading' }
  | { kind: 'ok'; cart: CartDto }
  | { kind: 'error'; message: string };

export function AdverseReviewLanding(): JSX.Element {
  const params = useParams<{ cartId: string }>();
  const cartId = params.cartId ?? '';
  const { client, makeSignal } = useApi();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoreSubmitting, setRestoreSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (!cartId) {
      setState({ kind: 'error', message: 'Missing cart reference.' });
      return;
    }
    const signal = makeSignal();
    client.retail.carts.get(cartId, { signal }).then(
      (res) => {
        if (!res.success || !res.cart) {
          setState({ kind: 'error', message: res.error ?? 'Cart not found.' });
          return;
        }
        setState({ kind: 'ok', cart: res.cart });
      },
      (err: unknown) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Cart not found.',
        });
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId]);

  const applicant = useMemo(
    () => (state.kind === 'ok' ? summarizePrimaryApplicant(state.cart) : null),
    [state],
  );

  const titleSuffix = applicant?.isEmpty ? 'an anonymous applicant' : applicant?.label ?? '';
  const resolvedAt = state.kind === 'ok' ? state.cart.resolvedAt : undefined;

  async function handleRestore(): Promise<void> {
    if (state.kind !== 'ok') return;
    setRestoreSubmitting(true);
    setRestoreError(null);
    try {
      const res: RestoreCartResponse = await client.retail.carts.restore(state.cart.cartId, {
        signal: makeSignal(),
      });
      if (!res.success) {
        setRestoreError(formatRestoreError(res));
        return;
      }
      if (res.cart) setState({ kind: 'ok', cart: res.cart });
    } catch (err) {
      if (ApiError.isApiError(err)) {
        // Backend may return 410 with the retention payload as either
        // ApiError details or the body — surface either path.
        const details = (err.details ?? {}) as Partial<RestoreCartResponse>;
        setRestoreError(
          err.code === 'RETENTION_WINDOW_EXPIRED'
            ? formatRestoreError({
                success: false,
                error: err.message,
                errorCode: 'RETENTION_WINDOW_EXPIRED',
                daysSinceResolved: details.daysSinceResolved,
                retentionDays: details.retentionDays,
                originRole: details.originRole,
                resolutionCode: details.resolutionCode,
              })
            : err.message,
        );
        return;
      }
      if (err instanceof Error && err.name === 'AbortError') return;
      setRestoreError(err instanceof Error ? err.message : 'Restore failed.');
    } finally {
      setRestoreSubmitting(false);
    }
  }

  return (
    <div>
      <header
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
          marginBottom: 'var(--sp-8)',
          paddingBottom: 'var(--sp-5)',
          borderBottom: '1px solid var(--surface-border)',
        }}
      >
        <Link
          to="/compliance/adverse-action-queue"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            fontSize: 12,
            color: 'var(--pcb-blue)',
            textDecoration: 'none',
            width: 'fit-content',
          }}
        >
          <ChevronLeft size={14} /> Back to Adverse-Action queue
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-5)', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0 }}>
            {state.kind === 'ok'
              ? `Adverse-Action review for ${titleSuffix}`
              : 'Adverse-Action review'}
          </h1>
          {state.kind === 'ok' && state.cart.originRole && (
            <Pill variant="info">{humanizeOriginTier(state.cart.originRole)}</Pill>
          )}
          {resolvedAt && (
            <span style={{ fontSize: 12, color: 'var(--ink-600)' }}>
              Resolved {new Date(resolvedAt).toLocaleString()}
            </span>
          )}
        </div>
        {state.kind === 'ok' && state.cart.resolutionReason && (
          <p
            style={{
              margin: 0,
              padding: 'var(--sp-4)',
              background: 'var(--surface-card)',
              border: '1px solid var(--surface-border)',
              borderRadius: 'var(--r-md)',
              fontSize: 13,
              color: 'var(--ink-800)',
              maxWidth: 720,
            }}
          >
            <strong style={{ display: 'block', marginBottom: 'var(--sp-2)' }}>
              Reason on file
            </strong>
            {state.cart.resolutionReason}
          </p>
        )}
      </header>

      {state.kind === 'loading' && (
        <div style={{ fontSize: 13, color: 'var(--ink-600)' }}>Loading cart context…</div>
      )}

      {state.kind === 'error' && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-6)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-md)',
            fontSize: 13,
          }}
        >
          {state.message}
        </div>
      )}

      {state.kind === 'ok' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 'var(--sp-6)',
            maxWidth: 1080,
          }}
        >

          <Card title="Restore cart">
            <p
              style={{
                margin: 0,
                fontSize: 13,
                color: 'var(--ink-700)',
                lineHeight: 1.5,
              }}
            >
              If this cart was closed in error, restore it back to <code>Open</code>. Restore
              is gated by the configured retention window — past the window the policy must
              be tuned in <em>Admin → System → Cart retention policies</em> first.
            </p>
            <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center' }}>
              <Button
                variant="ghost"
                onClick={() => {
                  void handleRestore();
                }}
                disabled={restoreSubmitting}
              >
                {restoreSubmitting ? 'Restoring…' : 'Restore to Open'}
              </Button>
            </div>
            {restoreError && (
              <div
                role="alert"
                style={{
                  padding: 'var(--sp-4)',
                  background: 'var(--err-bg)',
                  color: 'var(--err-text)',
                  borderRadius: 'var(--r-md)',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {restoreError}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--surface-border)',
        borderRadius: 'var(--r-md)',
        padding: 'var(--sp-6)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
        boxShadow: 'var(--sh-1)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 14, color: 'var(--pcb-blue-dark)' }}>{title}</h2>
      {children}
    </section>
  );
}

function formatRestoreError(res: RestoreCartResponse): string {
  if (res.errorCode === 'RETENTION_WINDOW_EXPIRED' && res.daysSinceResolved && res.retentionDays) {
    const tier = humanizeOriginTier(res.originRole);
    const code = res.resolutionCode ?? 'this resolution code';
    return (
      `This cart was closed ${res.daysSinceResolved} days ago and the retention ` +
      `window for ${tier} / ${code} is ${res.retentionDays} days. To extend ` +
      `retention, an admin can adjust the policy in Admin → System → Cart retention policies.`
    );
  }
  return res.error ?? 'Restore failed.';
}
